import React, { useState } from "react";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

const ReviewsSectionEN = () => {
  const [currentReview, setCurrentReview] = useState(0);

  // Authentic client reviews - English translations
  const reviews = [
    {
      name: "Florence",
      rating: 5,
      comment: "Perfect welcome, charming accommodation.",
      date: "July 2024",
      verified: true
    },
    {
      name: "Laurent",
      rating: 5,
      comment: "Great terrace, absolute calm. The donkey is amazing!",
      date: "August 2024",
      verified: true
    },
    {
      name: "Ruth & Alain",
      rating: 5,
      comment: "Ideal for hiking, very well located accommodation.",
      date: "June 2024",
      verified: true
    },
    {
      name: "Marie-Claire",
      rating: 5,
      comment: "Authentic and very comfortable gîte. We loved the garden and the pétanque court!",
      date: "September 2024",
      verified: true
    },
    {
      name: "Thomas",
      rating: 5,
      comment: "Excellent family stay. The children loved the outdoor activities.",
      date: "July 2024",
      verified: true
    },
    {
      name: "Isabelle",
      rating: 5,
      comment: "Very beautiful place, calm and peaceful. Perfect for recharging in Normandy.",
      date: "May 2024",
      verified: true
    }
  ];

  const nextReview = () => {
    setCurrentReview((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        size={16}
        className={index < rating ? "text-yellow-400 fill-current" : "text-gray-300"}
      />
    ));
  };

  return (
    <section 
      id="reviews" 
      className="section-padding bg-[#EDE6DB]/20"
      aria-labelledby="reviews-title"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <h2 
            id="reviews-title"
            className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-[#1C3A27] mb-4 md:mb-6"
          >
            Guest Reviews
          </h2>
          <p className="text-lg md:text-xl text-[#1C3A27]/70 max-w-3xl mx-auto leading-relaxed">
            Discover authentic testimonials from our guests
          </p>
          <a
            href="https://share.google/n5z0c02ew27vZ27UZ"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 md:gap-3 mt-6 bg-white rounded-full pl-4 pr-5 py-2 shadow-md border border-[#EDE6DB]/60 hover:shadow-lg transition-shadow focus:outline-none focus:ring-2 focus:ring-[#1C3A27] focus:ring-offset-2"
            aria-label="See Google reviews: 5 out of 5, 8 reviews (opens in a new tab)"
          >
            <span className="flex items-center gap-0.5">{renderStars(5)}</span>
            <span className="text-sm font-semibold text-[#1C3A27]">5.0</span>
            <span className="text-sm text-[#1C3A27]/60">· 8 Google reviews</span>
            <span className="text-sm font-medium text-[#1C3A27] underline underline-offset-2">See reviews</span>
          </a>
        </div>

        {/* Slider for mobile and tablet */}
        <div className="block lg:hidden mb-8">
          <div className="relative bg-white rounded-2xl p-6 md:p-8 shadow-xl border border-[#EDE6DB]/50 max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={prevReview}
                className="w-10 h-10 rounded-full bg-[#1C3A27]/10 hover:bg-[#1C3A27] hover:text-white transition-colors duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#1C3A27] focus:ring-offset-2"
                aria-label="Previous review"
              >
                <ChevronLeft size={20} />
              </button>
              
              <div className="text-center">
                <div className="flex justify-center mb-2">
                  {renderStars(reviews[currentReview].rating)}
                </div>
                <div className="text-sm text-[#1C3A27]/60">
                  {currentReview + 1} / {reviews.length}
                </div>
              </div>

              <button
                onClick={nextReview}
                className="w-10 h-10 rounded-full bg-[#1C3A27]/10 hover:bg-[#1C3A27] hover:text-white transition-colors duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#1C3A27] focus:ring-offset-2"
                aria-label="Next review"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div className="text-center">
              <Quote size={32} className="text-[#1C3A27]/20 mx-auto mb-4" />
              <blockquote className="text-lg text-[#1C3A27]/80 italic mb-4 leading-relaxed">
                "{reviews[currentReview].comment}"
              </blockquote>
              <div className="font-semibold text-[#1C3A27] text-lg">
                {reviews[currentReview].name}
              </div>
              <div className="text-sm text-[#1C3A27]/60">
                {reviews[currentReview].date}
              </div>
            </div>
          </div>
        </div>

        {/* Grid for desktop */}
        <div className="hidden lg:grid lg:grid-cols-2 xl:grid-cols-3 gap-6 lg:gap-8">
          {reviews.map((review, index) => (
            <div
              key={index}
              className="bg-white rounded-xl p-6 shadow-lg border border-[#EDE6DB]/50 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-1">
                  {renderStars(review.rating)}
                </div>
                <div className="text-xs text-[#1C3A27]/60">
                  {review.date}
                </div>
              </div>
              
              <Quote size={20} className="text-[#1C3A27]/20 mb-3" />
              <blockquote className="text-[#1C3A27]/80 italic mb-4 leading-relaxed">
                "{review.comment}"
              </blockquote>
              
              <div className="flex items-center">
                <div className="w-10 h-10 bg-[#1C3A27]/10 rounded-full flex items-center justify-center mr-3">
                  <span className="text-[#1C3A27] font-semibold text-sm">
                    {review.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-[#1C3A27]">
                    {review.name}
                  </div>
                  <div className="text-xs text-[#1C3A27]/60">
                    Verified guest
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Statistics */}
        <div className="mt-12 md:mt-16">
          <div className="bg-gradient-to-br from-[#1C3A27] to-[#2A5236] rounded-2xl p-8 text-white text-center max-w-4xl mx-auto">
            <h3 className="font-serif text-2xl font-bold mb-6">
              Our guests trust us
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <div>
                <div className="text-3xl md:text-4xl font-bold mb-2">4.8/5</div>
                <div className="flex justify-center mb-2">
                  {renderStars(5)}
                </div>
                <div className="text-white/80 text-sm">Average rating</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold mb-2">95%</div>
                <div className="text-white/80 text-sm">Satisfied guests</div>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold mb-2">3 keys</div>
                <div className="text-white/80 text-sm">Clévacances</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA to booking platforms */}
        <div className="mt-8 text-center">
          <p className="text-[#1C3A27]/70 mb-4">
            Check more reviews on our partner platforms
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://www.airbnb.fr/rooms/1124426465764074190"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-6 py-3 text-center hover:bg-[#1C3A27] hover:text-white transition-colors"
              aria-label="View reviews on Airbnb"
            >
              📱 Airbnb Reviews
            </a>
            <a
              href="https://www.booking.com/hotel/fr/gite-au-coeur-de-la-suisse-normande.fr.html"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary px-6 py-3 text-center hover:bg-[#1C3A27] hover:text-white transition-colors"
              aria-label="View reviews on Booking"
            >
              🏨 Booking Reviews
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewsSectionEN;